import React, { useEffect, useState } from 'react';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Button } from 'design-system/Form';
import UISelect from 'corp/components/UI/Select';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import { TextInput, Text, CircularProgress } from 'design-system';
import {
  BEE_TTS_PROTOCOLS,
  BEE_TTS_LOCALES,
  BEE_TTS_EMAIL_TO_USERID,
} from '@scale/shared/textcollection/beeTTSHelpers';
import { requiredAsterisk as defaultAsterisk } from 'react-scale/corp/helpers';

const description = `
### Description:
- This tool allows you to upload a batch of tasks
- Select the protocol, locale and project.
- Fill the audio folder URL, link to TXT and expected inputs. Click on Upload button

##### Author: Carlos Gonzalez
---
`;

interface SelectOption {
  label: string;
  value: any;
}

// eslint-disable-next-line complexity
const UploadTasks = () => {
  const [project, setProject] = useState<any>();
  const [projectOptions, setProjectOptions] = useState<SelectOption | null>(null);
  const [customer, setCustomer] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [projectValidation, setProjectValidation] = useState<{ valid: boolean; message: string }>({
    valid: true,
    message: '',
  });
  const [loadingProjectValidation, setLoadingProjectValidation] = useState<boolean>(false);
  const [batch, setBatch] = useState<string | null>();
  const [radarID, setRadarID] = useState<string | null>();
  const [batchTasks, setBatchTasks] = useState<number | null>(null);
  const [loadingBatchTasks, setLoadingBatchTasks] = useState(false);
  const [locale, setLocale] = useState<string | null>();
  const [protocol, setProtocol] = useState<string | null>();
  const [template, setTemplate] = useState<any>(false);
  const [loadingTemplate, setLoadingTemplate] = useState<boolean>(false);
  const [audioURL, setAudioURL] = useState<string>('');
  const [audios, setAudios] = useState<number | null>(null);
  const [path, setPath] = useState<string>('');
  const [loadingAudios, setLoadingAudios] = useState<boolean>(false);
  const [txtURL, setTXTURL] = useState<string>('');
  const [transcriptionsMsg, setTranscriptionsMsg] = useState<string | null>(null);
  const [transcriptionsError, setTranscriptionsError] = useState<boolean>(false);
  const [empty, setEmpty] = useState<number | null>(null);
  const [loadingTXT, setLoadingTXT] = useState<boolean>(false);
  const [expected, setExpected] = useState<number>(0);
  const [inputError, setInputError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const enabled = project && !!audioURL;

  useEffect(() => {
    if (project) {
      validateProject();
    }
  }, [project]);

  useEffect(() => {
    if (protocol && locale) {
      validateTemplateTask();
      validateProject();
    }
  }, [protocol, locale]);

  useEffect(() => {
    async function loadProjects() {
      const query = {
        includeTestProjects: true,
        exclusivelySyntheticProjects: false,
        customer,
        search,
      };
      const results = await apiFetch(null, 'corp-api/find/projects', { query });

      const options =
        results.projects.map((project: any) => {
          const value = project.id ?? project._id;
          const label = `${project.name} (${value})`;
          return { label, value };
        }) ?? [];
      setProjectOptions(options);
    }

    loadProjects();
  }, [customer, search]);

  const validateProject = async () => {
    if (project) {
      setLoadingProjectValidation(true);
      const res = await apiFetch(null, `corp-api/bee-tts-utils/validateProject/${project.value}`, {
        method: 'GET',
        query: { protocol, locale },
      });
      setProjectValidation(res);
      setLoadingProjectValidation(false);
    }
  };

  const validateTemplateTask = async () => {
    setLoadingTemplate(true);
    const res = await apiFetch(null, 'corp-api/bee-tts-utils/validateTemplate', {
      method: 'POST',
      body: { protocol, locale },
    });
    if (res.template) {
      res.template.batchName = res.batchName;
    }
    setTemplate(res.template);
    setLoadingTemplate(false);
  };

  const getAudioFiles = async () => {
    setLoadingAudios(true);
    try {
      const res = await apiFetch(null, 'corp-api/bee-tts-utils/filesCount', {
        method: 'GET',
        query: { url: audioURL },
      });
      setAudios(res.filesCount);
      setPath(res.path);
    } catch (err) {
      setAudios(null);
    }
    setLoadingAudios(false);
  };

  const getTXTFile = async () => {
    setLoadingTXT(true);
    try {
      const res = await apiFetch(null, 'corp-api/bee-tts-utils/txtCount', {
        method: 'GET',
        query: { url: txtURL, audioUrls: audios, protocol },
      });
      setTranscriptionsMsg(res.transcriptionsMsg);
      setEmpty(res.empty);
      setTranscriptionsError(res.error);
    } catch (err) {
      setTranscriptionsMsg(null);
      setEmpty(null);
      setTranscriptionsError(false);
    }
    setLoadingTXT(false);
  };

  const getBatchTasks = async () => {
    setLoadingBatchTasks(true);
    try {
      const res = await apiFetch(null, 'corp-api/bee-tts-utils/batchTasks', {
        method: 'GET',
        query: { projectId: project.value, batchName: batch },
      });
      setBatchTasks(res.tasks);
    } catch (err) {
      setBatchTasks(null);
    }
    setLoadingBatchTasks(false);
  };

  const handleSelectCustomer = (event: { value: string }) => {
    setCustomer(event?.value || '');
    setProject(null);
    setSearch('');
  };

  const handleRadarID = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setRadarID(event.target.value.trim());
  };

  const handleBatch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setBatch(event.target.value.trim());
  };

  const handleUpload = async () => {
    setLoading(true);
    setMessage('');
    // eslint-disable-next-line no-console
    console.log(`Uploading tasks for project ${project}`);

    try {
      const res = await apiFetch(null, 'corp-api/bee-tts-utils/upload', {
        method: 'POST',
        body: {
          protocol,
          locale,
          projectId: project.value,
          radarID,
          batchName: batch,
          audioURL,
          txtURL,
          expected,
          user: customer,
        },
      });

      setLoadingTemplate(false);
      setAudioURL('');
      setPath('');
      setTXTURL('');
      setAudios(null);
      setTranscriptionsMsg(null);
      setTranscriptionsError(false);
      setBatch(null);
      setExpected(0);
      setLoading(false);
      setInputError('');
      setMessage(res.message);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setLoading(false);
    }
  };

  const handleProjectChange = (selected: any) => {
    setProject(selected);
  };

  const buttonText = () => {
    if (!project) return 'Select project';
    if (!audioURL || !!inputError) return 'Input audio URL';
    if (!audios) return 'Validate audio folder';
    if (!!txtURL && (!transcriptionsMsg || transcriptionsError)) return 'Validate txt file';
    return 'Upload tasks';
  };

  const renderOutput = () => {
    return (
      <div className="response-markdown">
        <SanitizedMarkdown text={message || ''} />
        <style jsx global>{`
          .response-markdown tr {
            border: 1px solid rgba(0, 0, 0, 0.3);
          }

          .response-markdown tr:nth-child(even) {
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgb(0, 0, 0, 0.3);
            padding: 6px 10px;
            background-color: #f2f2f2;
          }

          .response-markdown th {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 6px 10px;
            background-color: #222;
            color: white;
          }

          .response-markdown td {
            border: 1px solid rgba(0, 0, 0, 0.3);
            padding: 10px;
          }
        `}</style>
      </div>
    );
  };

  const onChangeAudioURL = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setAudioURL(event.target.value.trim());
  };

  const onChangeTXTURL = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setTXTURL(event.target.value.trim());
  };

  const onChangeExpected = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setExpected(Number(event.target.value.trim()));
  };

  const numbersInPath = /\d+/g.exec(path);
  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>

      <section className="container">
        <div className="textTitle">
          <Text medium>Protocol</Text> {defaultAsterisk}
        </div>
        <UISelect
          className="input-component"
          value={protocol}
          onChange={(event: { value: string }) => setProtocol(event?.value || '')}
          placeholder="Select a protocol"
          options={Object.entries(BEE_TTS_PROTOCOLS).map(([_protocol, value]) => ({
            value,
            label: value,
          }))}
        />
      </section>
      <section className="container">
        <div className="textTitle">
          <Text medium>Locale</Text> {defaultAsterisk}
        </div>
        <UISelect
          className="input-component"
          value={locale}
          onChange={(event: { value: string }) => setLocale(event?.value || '')}
          placeholder="Select a locale"
          options={Object.entries(BEE_TTS_LOCALES).map(([_locale, value]) => ({
            value,
            label: value,
          }))}
        />
        {protocol && locale && !loadingTemplate && (
          <p className="template-validation">
            {template
              ? `✅ Template task exists: ${template.batchName || ''} -  ${template._id}`
              : '❌ Template task not found'}
          </p>
        )}
      </section>
      <section className="container">
        <div className="textTitle">
          <Text medium>Customer</Text> {defaultAsterisk}
        </div>
        <UISelect
          className="input-component"
          key={`${customer}-projects`}
          value={customer}
          onChange={handleSelectCustomer}
          placeholder="Select a customer"
          options={Object.entries(BEE_TTS_EMAIL_TO_USERID).map(([email, userID]) => ({
            value: userID,
            label: email,
          }))}
        />
      </section>
      {customer != null && customer !== '' && (
        <section className="container">
          <div className="textTitle">
            <Text medium>Project</Text> {defaultAsterisk}
          </div>
          <UISelect
            className="input-component"
            key={`${customer}-projects`}
            value={project}
            onChange={handleProjectChange}
            onInputChange={(value: string) => setSearch(value || '')}
            placeholder={'Project Name or ID'}
            options={projectOptions}
          />
          {project && !loadingProjectValidation && (
            <p className="template-validation">
              {projectValidation.valid
                ? '✅ Project name is valid'
                : `❌ ${projectValidation.message}`}
            </p>
          )}
        </section>
      )}
      <section className={project && !loadingAudios ? 'container' : 'container disabled'}>
        <div className="textTitle">
          <Text medium>Audio Folder URL</Text> {defaultAsterisk}
        </div>
        <div className="file-validation">
          <div className="input-component">
            <TextInput value={audioURL} onChange={onChangeAudioURL} disabled={!project} fullWidth />
            {audioURL && !loadingAudios && audios !== null && (
              <>
                <strong>Found {audios} audio files</strong>
              </>
            )}
          </div>
          <Button disabled={!audioURL || loadingAudios} onClick={getAudioFiles}>
            Validate Folder
          </Button>
        </div>
        {path && (
          <>
            Path: <i>{path}</i>
          </>
        )}
      </section>
      {!!protocol &&
        ['PV', 'PV-II', 'WebAnswers', 'PV-Audio', 'PV-Prosody', 'ABX-Large', 'ABX-Ref'].includes(
          protocol,
        ) && (
          <section className={project && audios ? 'container' : 'container disabled'}>
            <div className="textTitle">
              <Text medium>TXT/CSV File URL</Text> {defaultAsterisk}
            </div>
            <div className="file-validation">
              <div className="input-component">
                <TextInput
                  value={txtURL}
                  onChange={onChangeTXTURL}
                  disabled={!project || !audios}
                  fullWidth
                />
                {txtURL && !loadingTXT && transcriptionsMsg !== null && (
                  <>
                    <strong>{transcriptionsMsg}</strong>
                    {transcriptionsMsg && empty !== null && (
                      <>
                        <br />
                        <i>Empty lines: {empty}</i>
                      </>
                    )}
                  </>
                )}
              </div>
              <Button disabled={!txtURL || loadingTXT || !audios} onClick={getTXTFile}>
                Validate File
              </Button>
            </div>
          </section>
        )}
      <section className={project ? 'container' : 'container disabled'}>
        <div className="textTitle">
          <Text medium>Radar ID</Text> {defaultAsterisk}
        </div>
        <TextInput
          className="input-component"
          value={radarID}
          disabled={!project}
          onChange={handleRadarID}
          fullWidth
        />
        {radarID && numbersInPath && !numbersInPath.includes(radarID) && (
          <div className={'hint'}>⚠️ Current radar ID is not present in audio path</div>
        )}
      </section>
      <section className={project && radarID ? 'container' : 'container disabled'}>
        <div className="textTitle">
          <Text medium>Batch Name</Text> {defaultAsterisk}
        </div>
        <div className="file-validation">
          <div className="input-component">
            <TextInput
              value={batch}
              disabled={!project || !radarID}
              onChange={handleBatch}
              fullWidth
            />
          </div>
          <Button disabled={!batch || loadingBatchTasks} onClick={getBatchTasks}>
            Validate Batch
          </Button>
        </div>
        {batch && batchTasks !== null && !loadingBatchTasks && (
          <strong>
            {batchTasks === 0
              ? '✅ Batch name is available'
              : `⚠️Batch already exists with ${batchTasks} tasks`}
          </strong>
        )}
        {loadingBatchTasks && (
          <>
            <CircularProgress color="inherit" className="loader" />
            Validating batch name
          </>
        )}
      </section>
      <section className={project ? 'container' : 'container disabled'}>
        <div className="textTitle">
          <Text medium>Expected # of tasks</Text> {defaultAsterisk}
          {!!audios && !!expected && audios % expected !== 0 && (
            <div className="hint">
              ⚠️Number of files in folder ({audios}) does not match the number of expected tasks (
              {expected})
            </div>
          )}
        </div>
        <TextInput
          className="input-component"
          value={expected}
          onChange={onChangeExpected}
          inputProps={{
            step: 5,
            min: 0,
            type: 'number',
          }}
          fullWidth
        />
      </section>
      <br />
      <section>
        <Button
          primary
          disabled={
            !enabled ||
            !audioURL ||
            !!inputError ||
            !expected ||
            loading ||
            loadingAudios ||
            loadingTXT ||
            (!!txtURL && !transcriptionsMsg) ||
            !!transcriptionsError
          }
          requireConfirmation
          onClick={handleUpload}
        >
          {buttonText()}
        </Button>
        {message && renderOutput()}
      </section>
      <style jsx>{`
        .container {
          margin: 32px 0;
          width: 50%;
        }
        hint: {
          color: red;
          opacity: 0.5;
        }
        .template-validation {
          margin: 0;
        }
        .disabled {
          color: var(--color-RemoGray50);
        }
        .file-validation {
          display: flex;
          justify-content: space-between;
        }
        .input-component {
          width: 75%;
        }
        .loader {
          height: 2px;
          width: 2px;
        }
      `}</style>
    </div>
  );
};

export default UploadTasks;
